import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { toast } from 'sonner';
import { Heart, MessageCircle, Search, Award, X, Trophy, Loader2, Medal } from 'lucide-react';
import { format } from 'date-fns';
import LeaderboardPage from '@/components/LeaderboardPage';
import ImageWithLoader from '@/components/ImageWithLoader';
import { Skeleton } from '@/components/ui/skeleton';
import { useCache } from '@/context/CacheContext';

const MedalIcon = ({ count }) => {
  if (count === 0) return null;
  return (
    <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-semibold" data-testid="medal-badge">
      <Award size={12} />
      <span>{count}</span>
    </div>
  );
};

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${BACKEND_URL}${path}`;
};

export default function CommunityTab({ auth }) {
  const navigate = useNavigate();
  const { fetchCommunityFeed, fetchMonthlyLeaderboard, invalidate } = useCache();
  const [feed, setFeed] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [showEntryDetail, setShowEntryDetail] = useState(false);
  const [userMedals, setUserMedals] = useState({});
  const [currentMonthLeaderboard, setCurrentMonthLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [likesList, setLikesList] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    loadFeed(0, true);
    loadCurrentMonthLeaderboard();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadFeed = async (pageNum = 0, reset = false) => {
    if (isLoading) return;
    setIsLoading(true);
    setIsError(false);

    try {
      const limit = 10;
      const skip = pageNum * limit;

      const { entries: newEntries, fullFeed } = await fetchCommunityFeed(skip, limit, reset);

      if (newEntries.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setFeed(fullFeed);
      setPage(pageNum);

      // Collect medals data
      const medalsData = { ...userMedals };
      // We process only newEntries for medals data update to keep it efficient?
      // Or just process fullFeed. fullFeed is safer for consistency.
      fullFeed.forEach(entry => {
        if (entry.user_medals_count !== undefined) {
          medalsData[entry.user_id] = entry.user_medals_count;
        }
      });
      setUserMedals(medalsData);

    } catch (error) {
      console.error('Failed to load feed', error);
      setIsError(true);
      toast.error("Failed to load feed");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = () => {
    loadFeed(page + 1);
  };

  const loadCurrentMonthLeaderboard = async () => {
    setLoadingLeaderboard(true);
    try {
      const now = new Date();
      // Adjust to Jan 2026 if before 2026
      let year = now.getFullYear();
      let month = now.getMonth() + 1;

      if (year < 2026) {
           year = 2026;
           month = 1;
      }

      const data = await fetchMonthlyLeaderboard(year, month);
      setCurrentMonthLeaderboard(data.leaderboard);
    } catch (error) {
      console.error('Failed to load current month leaderboard', error);
    } finally {
        setLoadingLeaderboard(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`${API}/users/search?q=${searchQuery}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Failed to search users', error);
    }
  };

  const handleUserClick = (user) => {
    navigate(`/user/${user.id}`);
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleLike = async (entryId) => {
    // Optimistic Update
    setFeed(currentFeed => currentFeed.map(entry => {
      if (entry.id === entryId) {
        const isLiked = !!entry.is_liked;
        const newIsLiked = !isLiked;
        const newCount = isLiked ? Math.max(0, entry.likes_count - 1) : entry.likes_count + 1;

        let newFeaturedLiker = entry.featured_liker;

        if (newIsLiked) {
            // If we just liked it, and it had 0 likes (now 1), we are definitely the featured liker.
            // Even if it had more, we can show ourselves as the featured liker for instant feedback.
            if (entry.likes_count === 0) {
                 newFeaturedLiker = {
                    name: auth.user.name,
                    surname: auth.user.surname
                 };
            }
        } else {
             // If we unliked it
             if (newCount === 0) {
                 newFeaturedLiker = null;
             }
             // If we were the featured liker and we unliked, we should remove ourselves.
             // But we don't know who else liked it without a fetch.
             // Falling back to "N likes" (via null featured_liker) is acceptable for optimistic UI.
             else if (entry.featured_liker && entry.featured_liker.name === auth.user.name && entry.featured_liker.surname === auth.user.surname) {
                 newFeaturedLiker = null;
             }
        }

        return {
          ...entry,
          is_liked: newIsLiked,
          likes_count: newCount,
          featured_liker: newFeaturedLiker
        };
      }
      return entry;
    }));

    try {
      await axios.post(
        `${API}/likes`,
        { entry_id: entryId },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      // No reload needed for optimistic UI
    } catch (error) {
      toast.error('Failed to like entry');
      // Could revert state here if strict consistency needed, but skipping for simplicity as requested
    }
  };

  const handleComment = async (entryId) => {
    if (!commentText.trim()) return;

    try {
      await axios.post(
        `${API}/comments`,
        { entry_id: entryId, text: commentText },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      setCommentText('');
      toast.success('Comment added!');

      // Invalidate cache and reload
      invalidate('communityFeed');
      loadFeed(0, true);

      if (showEntryDetail && selectedEntry) {
        const updatedEntry = await axios.get(`${API}/entries/${entryId}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
        setSelectedEntry(updatedEntry.data);
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const openEntryDetail = (entry) => {
    setSelectedEntry(entry);
    setShowEntryDetail(true);
  };

  const handleShowLikes = async (entry) => {
    if (entry.likes_count === 0) return;

    try {
      const response = await axios.get(`${API}/likes/entry/${entry.id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setLikesList(response.data.likes);
      setShowLikesModal(true);
    } catch (error) {
      toast.error('Failed to load likes');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  if (showFullLeaderboard) {
    return <LeaderboardPage auth={auth} onBack={() => setShowFullLeaderboard(false)} />;
  }

  return (
    <div className="p-4 space-y-6" data-testid="community-tab">
      {/* Header with search */}
      <Card className="p-6 glass relative z-20" data-testid="community-header">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">Community Feed</h2>
        <div className="relative" ref={searchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={20} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search members..."
            className="pl-10 pr-10"
            data-testid="search-input"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
              data-testid="clear-search-button"
            >
              <X size={20} />
            </button>
          )}
          {searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200" data-testid="search-results">
              {searchResults.map((user) => (
                <Link
                  key={user.id}
                  to={`/user/${user.id}`}
                  onClick={() => {
                      setSearchResults([]);
                      setSearchQuery('');
                  }}
                  className="w-full p-3 hover:bg-gray-50 flex items-center gap-3 border-b last:border-b-0 text-left"
                  data-testid={`search-result-${user.id}`}
                >
                  <Avatar>
                    {user.profile_picture ? (
                      <ImageWithLoader
                        src={getImageUrl(user.profile_picture)}
                        alt={user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <AvatarFallback>{user.name[0]}{user.surname[0]}</AvatarFallback>
                    )}
                  </Avatar>
                  <div className="text-left flex-1">
                    <div className="font-medium text-gray-900">{user.name} {user.surname}</div>
                    <div className="text-sm text-gray-500">{user.location}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Mini Leaderboard */}
      {(loadingLeaderboard || currentMonthLeaderboard.length > 0) && (
        <Card className="p-6 glass cursor-pointer hover:shadow-lg transition-shadow relative z-0" onClick={() => !loadingLeaderboard && setShowFullLeaderboard(true)} data-testid="mini-leaderboard">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Trophy className="text-amber-500" size={24} />
              Top 3 This Month
            </h3>
            <span className="text-sm text-teal-600 font-medium">View Full Leaderboard →</span>
          </div>
          <div className="space-y-3">
            {loadingLeaderboard ? (
               Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg">
                     <Skeleton className="w-8 h-8 bg-amber-200/50" />
                     <Skeleton className="w-10 h-10 rounded-full bg-amber-200/50" />
                     <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32 bg-amber-200/50" />
                        <Skeleton className="h-3 w-48 bg-amber-200/50" />
                     </div>
                  </div>
               ))
            ) : (
                currentMonthLeaderboard.slice(0, 3).map((user) => {
                  const rank = currentMonthLeaderboard.findIndex(u => u.completed_days === user.completed_days) + 1;
                  return (
                    <div key={user.user_id} className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg">
                      <div className="w-8 flex justify-center">
                        {rank === 1 && <Trophy className="text-amber-500" size={24} />}
                        {rank === 2 && <Medal className="text-gray-400" size={24} />}
                        {rank === 3 && <Medal className="text-amber-700" size={24} />}
                        {rank > 3 && <span className="font-bold text-gray-600">{rank}</span>}
                      </div>
                      <Link
                        to={`/user/${user.user_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="block relative"
                      >
                        <Avatar>
                        {user.profile_picture ? (
                          <ImageWithLoader
                            src={getImageUrl(user.profile_picture)}
                            alt={user.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <AvatarFallback>{user.name[0]}{user.surname[0]}</AvatarFallback>
                        )}
                      </Avatar>
                      </Link>
                    <div className="flex-1">
                      <Link
                        to={`/user/${user.user_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-semibold hover:underline text-gray-900"
                      >
                          {user.name} {user.surname}
                      </Link>
                      <div className="text-sm text-gray-600">{user.completed_days} of {user.total_days} days ({user.completion_percentage}%)</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      )}

      {/* Feed */}
      <div className="space-y-4" data-testid="feed-container">
        {isLoading && feed.length === 0 ? (
           Array.from({ length: 3 }).map((_, i) => (
              <Card key={`skeleton-${i}`} className="p-6 glass">
                  <div className="flex items-center gap-3 mb-4">
                     <Skeleton className="w-10 h-10 rounded-full bg-gray-200" />
                     <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32 bg-gray-200" />
                        <Skeleton className="h-3 w-24 bg-gray-200" />
                     </div>
                  </div>
                  <Skeleton className="w-full h-80 rounded-lg bg-gray-200 mb-4" />
                  <div className="space-y-2 mb-4">
                      <Skeleton className="h-3 w-full bg-gray-200" />
                      <Skeleton className="h-3 w-3/4 bg-gray-200" />
                  </div>
                  <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
                      <Skeleton className="h-6 w-12 bg-gray-200" />
                      <Skeleton className="h-6 w-12 bg-gray-200" />
                  </div>
              </Card>
           ))
        ) : (
          feed.map((entry) => (
          <Card key={entry.id} className="p-6 glass" data-testid={`feed-entry-${entry.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <Link to={`/user/${entry.user_id}`}>
                <Avatar className="cursor-pointer">
                  {entry.profile_picture ? (
                     <ImageWithLoader
                      src={getImageUrl(entry.profile_picture)}
                      alt={entry.user_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <AvatarFallback>{entry.user_name[0]}{entry.user_surname[0]}</AvatarFallback>
                  )}
                </Avatar>
              </Link>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/user/${entry.user_id}`}
                    className="font-semibold cursor-pointer hover:text-teal-600 text-gray-900"
                  >
                    {entry.user_name} {entry.user_surname}
                  </Link>
                  <MedalIcon count={userMedals[entry.user_id] || 0} />
                </div>
                <div className="text-sm text-gray-500">
                  {format(new Date(entry.date), 'MMMM d, yyyy')} • {entry.challenge_name}
                </div>
              </div>
            </div>

            {entry.image && (
              <div
                className="mb-4 cursor-pointer overflow-hidden rounded-lg"
                onClick={() => openEntryDetail(entry)}
              >
                <ImageWithLoader
                  src={getImageUrl(entry.image)}
                  alt="Challenge proof"
                  className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                  data-testid={`entry-image-${entry.id}`}
                />
              </div>
            )}

            {entry.comment && (
              <p className="text-gray-700 mb-4" data-testid={`entry-comment-${entry.id}`}>{entry.comment}</p>
            )}

            {entry.likes_count > 0 && (
              <div className="mb-3">
                <button
                  onClick={() => handleShowLikes(entry)}
                  className="text-sm text-gray-600 hover:text-gray-900 text-left"
                >
                  {entry.likes_count === 1 && entry.featured_liker ? (
                    <>Liked by <span className="font-semibold text-gray-900">{entry.featured_liker.name.trim()} {entry.featured_liker.surname.trim()}</span></>
                  ) : entry.featured_liker ? (
                    <>
                      Liked by <span className="font-semibold text-gray-900">{entry.featured_liker.name.trim()} {entry.featured_liker.surname.trim()}</span> and <span className="font-semibold text-gray-900">{entry.likes_count - 1} others</span>
                    </>
                  ) : (
                    <span className="font-semibold text-gray-900">{entry.likes_count} like{entry.likes_count !== 1 ? 's' : ''}</span>
                  )}
                </button>
              </div>
            )}

            <div className="flex items-center gap-6 pt-4 border-t">
              <button
                onClick={() => handleLike(entry.id)}
                className={`flex items-center gap-2 transition-colors ${entry.is_liked ? 'text-red-500' : 'text-gray-600 hover:text-red-500'}`}
                data-testid={`like-button-${entry.id}`}
              >
                <Heart size={20} fill={entry.is_liked ? "currentColor" : "none"} />
                <span>{entry.likes_count || 0}</span>
              </button>
              <button
                onClick={() => openEntryDetail(entry)}
                className="flex items-center gap-2 text-gray-600 hover:text-teal-500 transition-colors"
                data-testid={`comment-button-${entry.id}`}
              >
                <MessageCircle size={20} />
                <span>{entry.comments?.length || 0}</span>
              </button>
            </div>
          </Card>
        )))
        }

        {isError && feed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <p className="text-gray-800 mb-4">Unable to load posts.</p>
            <Button
              onClick={() => loadFeed(0, true)}
              variant="outline"
              className="min-w-[120px]"
            >
              Retry
            </Button>
          </div>
        ) : hasMore && feed.length > 0 && (
           <div className="flex justify-center py-4">
             <Button
               variant="outline"
               onClick={loadMore}
               disabled={isLoading}
               className="w-full max-w-xs"
             >
               {isLoading ? (
                 <>
                   <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                   Loading...
                 </>
               ) : (
                 'Load More'
               )}
             </Button>
           </div>
        )}
      </div>

      {/* Likes List Modal */}
      <Dialog open={showLikesModal} onOpenChange={setShowLikesModal}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Likes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {likesList.map((like, index) => (
              <Link
                key={index}
                to={`/user/${like.user_id}`}
                className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => setShowLikesModal(false)}
              >
                <Avatar>
                  {like.profile_picture ? (
                    <ImageWithLoader
                      src={getImageUrl(like.profile_picture)}
                      alt={like.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <AvatarFallback>{like.name[0]}{like.surname[0]}</AvatarFallback>
                  )}
                </Avatar>
                <div className="font-medium text-gray-900">{like.name} {like.surname}</div>
              </Link>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry Detail Modal */}
      <Dialog open={showEntryDetail} onOpenChange={setShowEntryDetail}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          {selectedEntry && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                   <Link to={`/user/${selectedEntry.user_id}`} onClick={() => setShowEntryDetail(false)}>
                     <Avatar className="w-8 h-8 cursor-pointer">
                      {selectedEntry.profile_picture ? (
                         <ImageWithLoader
                          src={getImageUrl(selectedEntry.profile_picture)}
                          alt={selectedEntry.user_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <AvatarFallback>{selectedEntry.user_name?.[0]}{selectedEntry.user_surname?.[0]}</AvatarFallback>
                      )}
                    </Avatar>
                   </Link>
                  <div className="flex flex-col">
                      <Link
                        to={`/user/${selectedEntry.user_id}`}
                        onClick={() => setShowEntryDetail(false)}
                        className="text-sm font-semibold cursor-pointer hover:underline text-gray-900"
                      >
                          {selectedEntry.user_name} {selectedEntry.user_surname}
                      </Link>
                      <span className="text-xs text-gray-500 font-normal">
                          {format(new Date(selectedEntry.date), 'MMMM d, yyyy')} • {selectedEntry.challenge_name}
                      </span>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {selectedEntry.image && (
                  <div className="bg-black/5 rounded-lg overflow-hidden flex items-center justify-center">
                     <ImageWithLoader
                        src={getImageUrl(selectedEntry.image)}
                        alt="Entry proof"
                        className="w-full max-h-[60vh] object-contain"
                      />
                  </div>
                )}

                <div className="flex flex-col gap-2 px-1">
                     <div className="flex items-center gap-4 text-gray-600">
                        <button
                            className={`flex items-center gap-1 transition-colors ${selectedEntry.is_liked ? "text-red-500" : "hover:text-red-500"}`}
                            onClick={() => handleLike(selectedEntry.id)}
                        >
                            <Heart size={20} className={selectedEntry.is_liked ? "fill-red-500" : ""} />
                            <span className="text-sm font-medium">{selectedEntry.likes_count}</span>
                        </button>
                        <div className="flex items-center gap-1">
                            <MessageCircle size={20} />
                            <span className="text-sm font-medium">{selectedEntry.comments?.length || 0}</span>
                        </div>
                    </div>
                </div>

                {selectedEntry.comment && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700">{selectedEntry.comment}</p>
                  </div>
                )}

                {/* Comments Section */}
                <div className="border-t pt-4">
                    <h4 className="font-semibold text-sm text-gray-800 mb-3">Comments</h4>

                    {selectedEntry.comments && selectedEntry.comments.length > 0 ? (
                        <div className="space-y-3 max-h-60 overflow-y-auto pr-2 mb-4">
                            {selectedEntry.comments.map((comment) => (
                                <div key={comment.id} className="bg-white border rounded-lg p-3 text-sm shadow-sm">
                                    <div className="flex justify-between items-start mb-1">
                                        <span className="font-semibold text-gray-900">{comment.user_name}</span>
                                        <span className="text-xs text-gray-500">{format(new Date(comment.created_at), 'MMM d, h:mm a')}</span>
                                    </div>
                                    <p className="text-gray-700">{comment.text}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic mb-4">No comments yet. Be the first!</p>
                    )}

                    <div className="flex gap-2">
                        <Textarea
                            placeholder="Write a comment..."
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            className="min-h-[60px] resize-none"
                        />
                        <Button
                            className="h-auto"
                            onClick={() => handleComment(selectedEntry.id)}
                            disabled={!commentText.trim()}
                        >
                            Post
                        </Button>
                    </div>
                </div>

              </div>
            </>
          )}
        </DialogContent>
      </Dialog>


    </div>
  );
}